import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
    Box,
    CircularProgress,
    Alert,
    Snackbar,
    TextField,
    Checkbox,
    Paper,
    Typography,
    Chip,
    InputAdornment,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    FormControlLabel,
    Switch,
    Avatar,
} from '@mui/material';
import {
    Search as SearchIcon,
    Check as CheckIcon,
    ArrowBack as ArrowBackIcon,
    Close as CloseIcon,
    CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import collectionService, {
    Collection as CollectionType,
    ProductSearchResult,
} from '../../../shared/services/collectionService';
import { getProductNames } from '../../../shared/services/productService';

const DEBOUNCE_MS = 350;

interface FormData {
    name: string;
    isActive: boolean;
    isDeleted: boolean;
}

export const AddEditCollection: React.FC = () => {
    const navigate = useNavigate();
    const { id: collectionId } = useParams<{ id: string }>();
    const isEdit = Boolean(collectionId);

    // ── Form state ────────────────────────────────────────────────────────────
    const [formData, setFormData] = useState<FormData>({
        name: '',
        isActive: true,
        isDeleted: false,
    });

    // ── Image state ───────────────────────────────────────────────────────────
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    // ── Product search state ──────────────────────────────────────────────────
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<ProductSearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState<ProductSearchResult[]>([]);

    // ── UI state ──────────────────────────────────────────────────────────────
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingCollection, setIsLoadingCollection] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const searchBoxRef = useRef<HTMLDivElement>(null);

    // ── Track originally loaded product IDs for edit mode (to detect removals) ──
    const [originalProductIds, setOriginalProductIds] = useState<number[]>([]);

    // ── Load collection for edit mode ─────────────────────────────────────────
    useEffect(() => {
        if (isEdit && collectionId) {
            loadCollection(collectionId);
        }
    }, [isEdit, collectionId]);

    const loadCollection = async (id: string) => {
        try {
            setIsLoadingCollection(true);

            const [colResponse, productsResponse] = await Promise.all([
                collectionService.getCollectionById(id),
                collectionService.listCollectionProducts(id, { limit: 1000 }),
            ]);

            const col = colResponse.data as CollectionType;
            if (col) {
                setFormData({
                    name: col.name,
                    isActive: col.isActive,
                    isDeleted: col.isDeleted,
                });
                if (col.imageUrl) {
                    setImagePreview(col.imageUrl);
                }
            }

            // Populate already-selected products
            let existingItems: any[] = [];
            const pData = productsResponse?.data as any;
            if (pData && typeof pData === 'object' && 'items' in pData && Array.isArray(pData.items)) {
                existingItems = pData.items;
            } else if (Array.isArray(pData)) {
                existingItems = pData;
            } else if (Array.isArray(productsResponse)) {
                existingItems = productsResponse as any;
            }

            if (existingItems.length > 0) {
                let nameMap: Record<number, string> = {};
                try {
                    const namesResponse = await getProductNames();
                    if (namesResponse.success && Array.isArray(namesResponse.data)) {
                        namesResponse.data.forEach((p) => { nameMap[p.productId] = p.name; });
                    }
                } catch {
                    // names are optional — silently ignore
                }

                const mapped: ProductSearchResult[] = existingItems.map((cp) => ({
                    productId: cp.productId,
                    name: nameMap[cp.productId] ?? `Product #${cp.productId}`,
                }));
                setSelectedProducts(mapped);
                setOriginalProductIds(mapped.map((p) => p.productId));
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to load collection');
        } finally {
            setIsLoadingCollection(false);
        }
    };

    // ── Close dropdown when clicking outside ──────────────────────────────────
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ── Image handling ────────────────────────────────────────────────────────
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ── Debounced product search ───────────────────────────────────────────────
    const handleSearchChange = useCallback((value: string) => {
        setSearchQuery(value);

        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        if (!value.trim()) {
            setSearchResults([]);
            setDropdownOpen(false);
            return;
        }

        debounceTimer.current = setTimeout(async () => {
            try {
                setIsSearching(true);
                const response = await collectionService.searchProducts(value.trim());
                const results = response.data ?? [];
                setSearchResults(results);
                setDropdownOpen(true);
            } catch {
                setSearchResults([]);
                setDropdownOpen(false);
            } finally {
                setIsSearching(false);
            }
        }, DEBOUNCE_MS);
    }, []);

    // ── Selection helpers ─────────────────────────────────────────────────────
    const isSelected = (productId: number) =>
        selectedProducts.some((p) => p.productId === productId);

    const toggleProduct = (product: ProductSearchResult) => {
        setSelectedProducts((prev) =>
            isSelected(product.productId)
                ? prev.filter((p) => p.productId !== product.productId)
                : [...prev, product]
        );
    };

    const handleSelectAll = () => {
        const allSelected = searchResults.every((r) => isSelected(r.productId));
        if (allSelected) {
            setSelectedProducts((prev) =>
                prev.filter((p) => !searchResults.some((r) => r.productId === p.productId))
            );
        } else {
            setSelectedProducts((prev) => {
                const existing = new Set(prev.map((p) => p.productId));
                const toAdd = searchResults.filter((r) => !existing.has(r.productId));
                return [...prev, ...toAdd];
            });
        }
    };

    const removeSelectedProduct = (productId: number) => {
        setSelectedProducts((prev) => prev.filter((p) => p.productId !== productId));
    };

    // ── Form change ───────────────────────────────────────────────────────────
    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!formData.name.trim()) {
            setError('Collection name is required');
            return;
        }

        try {
            setIsSubmitting(true);
            setError('');

            let savedCollectionId: string | undefined;

            if (isEdit && collectionId) {
                await collectionService.updateCollection(collectionId, formData, imageFile);
                savedCollectionId = collectionId;
                setSuccess('Collection updated successfully');
            } else {
                const response = await collectionService.createCollection(formData, imageFile);
                const created = response.data as CollectionType;
                savedCollectionId = created?.id;
                setSuccess('Collection created successfully');
            }

            // Sync products for both create and edit
            if (savedCollectionId) {
                const currentIds = selectedProducts.map((p) => p.productId);

                if (isEdit) {
                    const toRemove = originalProductIds.filter((id) => !currentIds.includes(id));
                    for (const productId of toRemove) {
                        await collectionService.removeProductFromCollection(savedCollectionId, productId);
                    }
                    const toAdd = currentIds.filter((id) => !originalProductIds.includes(id));
                    if (toAdd.length > 0) {
                        await collectionService.addProductToCollection(savedCollectionId, {
                            productId: toAdd.length === 1 ? toAdd[0] : toAdd,
                        });
                    }
                } else {
                    if (currentIds.length > 0) {
                        await collectionService.addProductToCollection(savedCollectionId, {
                            productId: currentIds.length === 1 ? currentIds[0] : currentIds,
                        });
                    }
                }
            }

            setTimeout(() => navigate('/collections'), 800);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save collection');
        } finally {
            setIsSubmitting(false);
        }
    };

    const allVisibleSelected =
        searchResults.length > 0 && searchResults.every((r) => isSelected(r.productId));

    if (isLoadingCollection) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex-1 min-w-0">
                    <div className="-ml-4 mb-2">
                        <Link to="/collections" className="back-link">
                            <ArrowBackIcon className="text-[#0EA5E9]" />
                            <span className="ml-2 text-[#0EA5E9]">Back</span>
                        </Link>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                        {isEdit ? 'Edit Collection' : 'New Collection'}
                    </h2>
                </div>
            </div>

            {/* Content */}
            <div className="flex flex-auto p-6 sm:p-10">
                <div className="w-full max-w-2xl mx-auto flex flex-col gap-6">

                    {/* Collection Details */}
                    <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} mb={2}>
                            Collection Details
                        </Typography>

                        <Box display="flex" flexDirection="column" gap={2}>
                            {/* Name */}
                            <TextField
                                autoFocus
                                label="Collection Name"
                                name="name"
                                value={formData.name}
                                onChange={handleFormChange}
                                fullWidth
                                required
                                placeholder="Enter collection name"
                            />

                            {/* Active toggle */}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={(e) =>
                                            setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
                                        }
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="body2" fontWeight={500}>
                                        {formData.isActive ? 'Active' : 'Inactive'}
                                    </Typography>
                                }
                            />
                        </Box>
                    </Paper>

                    {/* Image Upload */}
                    <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} mb={1}>
                            Collection Image
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Upload an image for this collection (optional).
                        </Typography>

                        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                            {/* Preview */}
                            {imagePreview ? (
                                <Box position="relative" display="inline-flex">
                                    <Avatar
                                        src={imagePreview}
                                        variant="rounded"
                                        sx={{ width: 96, height: 96, border: '1px solid', borderColor: 'divider' }}
                                    />
                                    <Box
                                        onClick={handleRemoveImage}
                                        sx={{
                                            position: 'absolute',
                                            top: -8,
                                            right: -8,
                                            bgcolor: 'error.main',
                                            color: '#fff',
                                            borderRadius: '50%',
                                            width: 22,
                                            height: 22,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: 14 }} />
                                    </Box>
                                </Box>
                            ) : (
                                <Avatar
                                    variant="rounded"
                                    sx={{
                                        width: 96,
                                        height: 96,
                                        bgcolor: 'grey.100',
                                        border: '1px dashed',
                                        borderColor: 'divider',
                                        color: 'text.secondary',
                                    }}
                                >
                                    <CloudUploadIcon />
                                </Avatar>
                            )}

                            <Box>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={handleImageChange}
                                />
                                <AppButton
                                    variant="secondary"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    {imagePreview ? 'Change Image' : 'Upload Image'}
                                </AppButton>
                                {imageFile && (
                                    <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                                        {imageFile.name}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Paper>

                    {/* Product Search */}
                    <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600} mb={1}>
                            Add Products
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Search and select products to add to this collection.
                        </Typography>

                        {/* Search Input + Dropdown */}
                        <div ref={searchBoxRef} style={{ position: 'relative' }}>
                            <TextField
                                fullWidth
                                placeholder="Search products by name (e.g. almond)"
                                value={searchQuery}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                onFocus={() => searchResults.length > 0 && setDropdownOpen(true)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            {isSearching ? (
                                                <CircularProgress size={18} />
                                            ) : (
                                                <SearchIcon fontSize="small" color="action" />
                                            )}
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchQuery ? (
                                        <InputAdornment position="end">
                                            <CloseIcon
                                                fontSize="small"
                                                sx={{ cursor: 'pointer', color: 'text.secondary' }}
                                                onClick={() => {
                                                    setSearchQuery('');
                                                    setSearchResults([]);
                                                    setDropdownOpen(false);
                                                }}
                                            />
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />

                            {/* Dropdown Results */}
                            {dropdownOpen && searchResults.length > 0 && (
                                <Paper
                                    elevation={4}
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        zIndex: 1300,
                                        maxHeight: 320,
                                        overflowY: 'auto',
                                        mt: 0.5,
                                        borderRadius: 1,
                                    }}
                                >
                                    {/* Select All row */}
                                    <ListItem disablePadding sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                                        <ListItemButton onClick={handleSelectAll} dense>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Checkbox
                                                    edge="start"
                                                    checked={allVisibleSelected}
                                                    indeterminate={
                                                        !allVisibleSelected &&
                                                        searchResults.some((r) => isSelected(r.productId))
                                                    }
                                                    tabIndex={-1}
                                                    disableRipple
                                                    size="small"
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body2" fontWeight={600}>
                                                        Select All ({searchResults.length})
                                                    </Typography>
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>

                                    <List disablePadding>
                                        {searchResults.map((product) => (
                                            <ListItem key={product.productId} disablePadding>
                                                <ListItemButton
                                                    dense
                                                    onClick={() => toggleProduct(product)}
                                                    selected={isSelected(product.productId)}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                                        <Checkbox
                                                            edge="start"
                                                            checked={isSelected(product.productId)}
                                                            tabIndex={-1}
                                                            disableRipple
                                                            size="small"
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="body2">
                                                                {product.name}
                                                            </Typography>
                                                        }
                                                        secondary={`ID: ${product.productId}`}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            )}

                            {/* No results message */}
                            {dropdownOpen && searchQuery.trim() && !isSearching && searchResults.length === 0 && (
                                <Paper
                                    elevation={4}
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        zIndex: 1300,
                                        mt: 0.5,
                                        borderRadius: 1,
                                        p: 2,
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary" textAlign="center">
                                        No products found for "{searchQuery}"
                                    </Typography>
                                </Paper>
                            )}
                        </div>

                        {/* Selected Products Chips */}
                        {selectedProducts.length > 0 && (
                            <Box mt={2}>
                                <Divider sx={{ mb: 1.5 }} />
                                <Typography variant="body2" color="text.secondary" mb={1}>
                                    Selected ({selectedProducts.length}):
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={1}>
                                    {selectedProducts.map((p) => (
                                        <Chip
                                            key={p.productId}
                                            label={p.name}
                                            size="small"
                                            onDelete={() => removeSelectedProduct(p.productId)}
                                            deleteIcon={<CloseIcon />}
                                            sx={{ maxWidth: 200 }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Paper>

                    {/* Actions */}
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <AppButton
                            variant="secondary"
                            onClick={() => navigate('/collections')}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </AppButton>
                        <AppButton
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            startIcon={
                                isSubmitting ? <CircularProgress size={20} /> : <CheckIcon />
                            }
                        >
                            {isSubmitting ? 'Saving...' : 'Save Collection'}
                        </AppButton>
                    </Box>
                </div>
            </div>

            {/* Snackbar */}
            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={() => { setError(''); setSuccess(''); }}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
